import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface InvestmentBannerItem {
  id: string | number;
  image: string;
  imageAlt?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  description: string;
  ctaText?: string;
  onCTAClick?: () => void;
}

interface InvestmentBannerProps {
  items?: InvestmentBannerItem[];
  currentIndex?: number;
  showIndicators?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  onItemChange?: (index: number) => void;
  className?: string;
}

const defaultItems: InvestmentBannerItem[] = [
  {
    id: 1,
    image: "/conten-photo.jpg",
    imageAlt: "Studio Universidade",
    title: "Studio Universidade.",
    subtitle:
      "Alugue Studios mobiliados pronto para morar. Viva perto da PUC Campinas, com toda a comodidade e segurança que sua rotina precisa.",
    description:
      "Mude para uma vida mais prática e conectada. A poucos minutos do campus da PUC Campinas no Parque das Universidades.",
    ctaText: "Quero saber mais",
    onCTAClick: () =>
      window.open("https://studio-universidade.housebit.com.br/", "_blank"),
  },
  {
    id: 2,
    image: "/soul-photo.png",
    imageAlt: "Soul Taquaral",
    title: "Soul Taquaral",
    subtitle:
      "More com exclusividade no residencial no bairro mais desejado de Campinas.",
    description: "Casas com varanda social, jardim privativo e lazer completo.",
    ctaText: "Quero saber mais",
    onCTAClick: () =>
      window.open("https://soul-taquaral.housebit.com.br/", "_blank"),
  },
];

const InvestmentBanner: React.FC<InvestmentBannerProps> = ({
  items = defaultItems,
  currentIndex = 0,
  showIndicators = true,
  autoPlay = true,
  autoPlayInterval = 8000, // Aumentado para 8 segundos
  onItemChange,
  className
}) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);

  const currentItem = items[activeIndex] || items[0];

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || items.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % items.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, items.length]);

  // Notify parent of changes
  useEffect(() => {
    onItemChange?.(activeIndex);
  }, [activeIndex, onItemChange]);

  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleCTAClick = () => {
    currentItem.onCTAClick?.();
  };

  return (
    <section className={cn("flex flex-col items-center gap-8 w-full lg:px-0", className)}>
      {/* Container Principal - Side by Side Layout seguindo Figma */}
      <div className="flex flex-col lg:flex-row lg:gap-20 gap-8 items-center lg:items-stretch lg:pr-20 w-full">
        {/* Imagem - Lado Esquerdo (Full-bleed no mobile) */}
        <div className="flex-1 w-full">
          <div className="lg:w-full lg:ml-0 lg:mr-0 w-[100vw] max-w-[100vw] ml-[calc(50%-50vw)] mr-[calc(50%-50vw)] h-full">
            <img
              src={currentItem.image}
              alt={currentItem.imageAlt || `Investment Banner ${activeIndex + 1}`}
              className="w-full h-56 lg:h-[420px] object-cover rounded-none transition-all duration-1000 ease-in-out"
              key={activeIndex} // Force re-render for smooth transition
            />
          </div>
        </div>

        {/* Conteúdo de Texto - Lado Direito */}
        <div className="flex-1 flex flex-col gap-6 items-start w-full lg:justify-center" key={`content-${activeIndex}`}>
          {/* Título e Subtítulo */}
          <div className="flex flex-col lg:gap-6 gap-4 items-start w-full animate-fade-in">
            <div className="w-full">
              {/* Título Principal */}
              <h2 className="font-black text-[24px] lg:text-[40px] leading-[1.4] text-neutral-black mb-0">
                {currentItem.title}
              </h2>

              {/* Subtítulo */}
              {currentItem.subtitle && (
                <p className="font-bold text-[18px] lg:text-[32px] leading-[1.4] text-neutral-black mt-2 lg:mt-0">
                  {currentItem.subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Descrição e CTA */}
          <div className="flex flex-col justify-center items-start gap-4 w-full animate-fade-in">
            <p className="w-full text-[16px] lg:text-[20px] font-normal leading-normal text-neutral-gray-dark">
              {currentItem.description}
            </p>

            {/* Botão CTA seguindo design Figma - Responsivo */}
            {currentItem.ctaText && (
              <button
                onClick={handleCTAClick}
              className="bg-gray-text h-[48px] lg:h-[54px] px-6 lg:w-[231px] rounded-[135px] flex items-center justify-center hover:bg-gray-text/90 transition-all duration-500 ease-in-out"
              >
              <span className="font-normal text-[18px] lg:text-[21.6px] leading-none text-white">
                  {currentItem.ctaText}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Indicadores do Carrossel */}
      {showIndicators && items.length > 1 && (
        <div className="flex items-center gap-3 h-2">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => handleIndicatorClick(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-700 ease-in-out",
                index === activeIndex
                  ? "bg-neutral-gray scale-110"
                  : "bg-neutral-gray-light/30 hover:bg-neutral-gray-light/50 scale-100"
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export { InvestmentBanner, type InvestmentBannerProps, type InvestmentBannerItem };
export default InvestmentBanner;